<script>
  import LocationControl from '../LocationControl/LocationControl.svelte';
  import ProviderSearchResultTile from '../ProviderSearchResultTile/ProviderSearchResultTile.svelte';
  import ProviderSearchResultSkeletonTile from '../ProviderSearchResultSkeletonTile/ProviderSearchResultSkeletonTile.svelte';
  import BaseHeading from '../BaseHeading/BaseHeading.svelte';
  import BaseLink from '../BaseLink/BaseLink.svelte';
  import searchValue from '../../stores/search';
  import { openOverpanel } from '../../stores/overpanelControl';
  import { SPRITESHEET_URL } from '../../utils/constants';
  import { RecentProviderSearches, RecentFacilitySearches, RecentPracticeSearches } from '../../utils/local-storage';
  import { trackEvent } from '../../utils/umami-events.js';
  
  let {
    clearResultTileButtonTextTemplate,
    errorText,
    recentSearchesText,
    clearRecentSearchesText,
    locationControl,
    setLocationErrorText,
    noResultsInLocationTextTemplate,
    id,
    lang,
    selected = $bindable(),
    isError,
    searchResults,
    numberOfResults,
    closeCallback = () => {},
    currentLocation,
    resultTiles = $bindable(),
    commonSpecialtiesList,
    providerSearchType,
    locationControlElem = $bindable(undefined),
    showMoreText,
    showLessText,
    dataTestId = 'provider-overpanel-results'
  } = $props();
  
  let recentSearches = $state([]);

  let RecentSearchesStore = RecentProviderSearches;
  
  if (providerSearchType === 'practice') {
    RecentSearchesStore = RecentPracticeSearches;
  } else if (providerSearchType === 'facility') {
    RecentSearchesStore = RecentFacilitySearches;
  }

  try {
    recentSearches = RecentSearchesStore.get();
  } catch {
    recentSearches = [];
  }
  
  const handleClearButtonClick = (search) => {
    RecentSearchesStore.remove(search);
    try {
      recentSearches = RecentSearchesStore.get();
    } catch {
      recentSearches = [];
    }
  };

  const handleClearAll = () => {
    RecentSearchesStore.clear();
    recentSearches = [];
  };

  const openLocationOverpanel = () => {
    openOverpanel('location', {
      closeCallback: () => {
        closeCallback();
      }
    })                      
  }

  let expanded = $state(false);
  const toggle = () => (expanded = !expanded);
</script>

<div data-testid={dataTestId}>
  <LocationControl {...locationControl} {closeCallback} inOverpanel={true} bind:locationControl={locationControlElem} />
  <ul {id} class="px-4 flex flex-col" aria-label="Search Results">
    {#each searchResults as group, index (index)}
      {#if group.id}  
        <li class="border-b border-gray-silver last:border-b-0" >
          {#if group.title}<h3 class="mt-4 font-semibold" class:hidden={numberOfResults===0}>{group.title}</h3>{/if}
          {#if group.results.length > 0}
            <ul class="mt-2 mb-6">
              {#each group.results as item, i (i)}
                <li id={`search-result-${group.id}-${i}`} class="mt-4" class:hidden={!expanded && i > 4} tab-index="-1" bind:this={resultTiles[i]}>
                  <ProviderSearchResultTile
                    {...item}
                    {clearResultTileButtonTextTemplate}
                    {lang}
                    {providerSearchType}
                    focusCallback={() => (selected = i)}
                    blurCallback={() => {
                      if (selected === i) {
                        selected = undefined;
                      }
                    }}
                  />
                </li>
              {/each}
              {#if group.results.length > 5}
                <button onclick={toggle} class="mt-4 group">
                  <div
                    class="flex items-center text-sm leading-relaxed font-semibold text-blue-cerulean hover-hover:hover:text-blue-darkish hover-hover:hover:underline active:underline active:text-blue-darkish"
                  >
                    <svg role="presentation" class="w-4 h-4 inline mr-2 fill-current">
                      <use xlink:href="{SPRITESHEET_URL}{!expanded ? 'plus-sign' : 'minus-sign'}" />
                    </svg>
                    <span>{!expanded ? showMoreText : showLessText}</span>
                  </div>
                </button>
              {/if}
            </ul>  
          {:else if group.id === 'providers'}
            <div class="border-b border-gray-silver py-4">
              {#if !currentLocation}
                <button 
                  class="italic text-left"
                  onclick={openLocationOverpanel}
                >
                  {@html setLocationErrorText}
                </button>
              {:else}
                <p>{noResultsInLocationTextTemplate.replace(/{{LOCATION}}/, `${currentLocation.city}, ${currentLocation.zip}`)} </p> 
              {/if}
            </div>            
          {/if}            
        </li>  
      {/if}
    {/each}
  </ul>
  {#if !numberOfResults}
    {#if isError}
      {#if providerSearchType !== 'provider'}
        <div data-testid="{dataTestId}-error-text" class="px-4 py-6 border-b">
          {#if providerSearchType === 'facility'}
            <p class="text-base leading-snug italic text-gray-gunmetal">{errorText}</p>
          {:else if currentLocation && currentLocation.city && currentLocation.zip}
            <p>{noResultsInLocationTextTemplate.replace(/{{LOCATION}}/, `${currentLocation.city}, ${currentLocation.zip}`)} </p>               
          {:else}
            <button 
              class="italic text-left"
              onclick={openLocationOverpanel}
            >
              {@html setLocationErrorText}
            </button>
          {/if}
        </div>
      {/if}
    {:else}
      {#if $searchValue}
        <ul class="p-4">
          {#each Array(5)}
            <li class="mb-4">
              <ProviderSearchResultSkeletonTile />
            </li>
          {/each}
        </ul>
      {/if}
    {/if}

    {#if isError || (!$searchValue && !isError)}
      <div class="pt-4 px-4">
        {#if recentSearches.length}
          <div class="flex flex-row justify-between items-center mb-2">
            <div class="text-base leading-loose text-blue-dark-navy">{recentSearchesText}</div>
            <button
              class="flex flex-row items-center text-xs text-blue-cerulean font-semibold leading-normal py-2 px-1
              hover-hover:hover:text-blue-darkish hover-hover:hover:underline active:underline active:text-blue-darkish"
              onclick={handleClearAll}
              data-testid="{dataTestId}-clear-all-button"
            >
              {clearRecentSearchesText}
              <svg class="ml-2 w-4 h-4 fill-current inline-block" role="presentation">
                <use xlink:href="{SPRITESHEET_URL}clear-x" />
              </svg>
            </button>
          </div>
          <ul>
            {#each recentSearches as item, index (index)}
              <li class="mb-4">
                <ProviderSearchResultTile {...item} {clearResultTileButtonTextTemplate} {lang} {handleClearButtonClick} {providerSearchType} />
              </li>
            {/each}
          </ul>
        {:else if commonSpecialtiesList && commonSpecialtiesList.commonSpecialties.length}
          <BaseHeading className="mb-2 text-base text-blue-dark-navy" headingLevel={commonSpecialtiesList.headingLevel}>{commonSpecialtiesList.headingTextTemplate}</BaseHeading>
          <ul class="flex flex-wrap pb-2">
            {#each commonSpecialtiesList.commonSpecialties as { name, url }, index (index)}
              <li class="inline-flex mr-3 mb-3" data-testid="{dataTestId}-popular-provider-item">
                <BaseLink
                  className="px-6 py-1 shadow-medium font-semibold rounded bg-white text-base leading-loose text-center text-blue-navy 
                  relative pseudo-shadow-tight-hover active:shadow-active"
                  {url}
                  onClick={() => trackEvent(`provider-search-common-specialty-link-click`, { value: name })}
                >
                  {name}
                </BaseLink>
              </li>
            {/each}
          </ul>
        {/if}
      </div>    
    {/if}
  {/if}
</div>
